import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const $ = (value) => (value === undefined || value === null ? "" : String(value));

export default function ExpenseFormModal({ isOpen, expense = null, meta, toggle, onSaved }) {
  const editing = Boolean(expense);
  const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting } } = useForm();
  const [companyBase, setCompanyBase] = useState("");

  const selectedCompany = watch("company_id");
  const net = watch("net_amount");
  const tax = watch("tax_amount");
  const gross = watch("gross_amount");

  useEffect(() => {
    if (editing) {
      setValue("company_id", $($expense(expense).company_id));
      setValue("title", $expense(expense).title);
      setValue("description", $expense(expense).description);
      setValue("category_id", $expense(expense).category_id);
      setValue("supplier_name", $expense(expense).supplier || $expense(expense).supplier_name);
      setValue("invoice_number", $expense(expense).invoice_number);
      setValue("receipt_reference", $expense(expense).receipt_reference);
      setValue("expense_date", $expense(expense).expense_date);
      setValue("invoice_date", $expense(expense).invoice_date);
      setValue("due_date", $expense(expense).due_date);
      setValue("net_amount", $expense(expense).net_amount);
      setValue("tax_amount", $expense(expense).tax_amount);
      setValue("gross_amount", $expense(expense).gross_amount);
      setValue("currency_code", $expense(expense).currency_code);
      setValue("payment_method", $expense(expense).payment_method);
      setValue("department", $expense(expense).department);
      setValue("cost_center", $expense(expense).cost_center);
      setCompanyBase(meta.companies.find((c) => c.id === Number($expense(expense).company_id))?.base_currency || "");
    }
  }, [editing]);

  useEffect(() => {
    if (!editing && selectedCompany) {
      const base = meta.companies.find((c) => c.id === Number(selectedCompany))?.base_currency;
      if (base) {
        setCompanyBase(base);
        setValue("currency_code", base);
      }
    }
  }, [selectedCompany, editing, meta.companies, setValue]);

  const calculatedGross = useMemo(() => {
    if (gross !== "" && gross !== undefined && gross !== null) return null;
    const n = Number(net);
    const t = tax === "" || tax === undefined || tax === null ? 0 : Number(tax);
    if (!Number.isFinite(n) || n <= 0) return null;
    return (n + t).toFixed(3);
  }, [net, tax, gross]);

  const categoriesQuery = useQuery({
    queryKey: ["expenseManagerCategories"],
    queryFn: ExpenseManagerService.getCategories,
    staleTime: 60_000,
  });

  const categoryOptions = useMemo(() => {
    const flat = [];
    const walk = (items, depth) => {
      (items || []).forEach((item) => {
        flat.push({ id: item.id, name: `${"— ".repeat(depth)}${item.name}` });
        walk(item.children, depth + 1);
      });
    };
    walk(categoriesQuery.data?.data, 0);
    return flat;
  }, [categoriesQuery.data]);

  const submit = async (values) => {
    const payload = {
      company_id: Number(values.company_id),
      title: values.title.trim(),
      description: values.description || null,
      category_id: values.category_id ? Number(values.category_id) : null,
      supplier_name: values.supplier_name || null,
      invoice_number: values.invoice_number || null,
      receipt_reference: values.receipt_reference || null,
      expense_date: values.expense_date,
      invoice_date: values.invoice_date || null,
      due_date: values.due_date || null,
      net_amount: String(Number(values.net_amount)),
      tax_amount: values.tax_amount === "" ? null : String(Number(values.tax_amount)),
      gross_amount: values.gross_amount === "" || values.gross_amount == null
        ? (Number(values.net_amount) + Number(values.tax_amount || 0)).toFixed(3)
        : String(Number(values.gross_amount)),
      currency_code: (values.currency_code || companyBase || "TND").toUpperCase(),
      payment_method: values.payment_method || null,
      department: values.department || null,
      cost_center: values.cost_center || null,
    };
    try {
      if (editing) {
        await ExpenseManagerService.updateExpense(expense.id, payload);
      } else {
        await ExpenseManagerService.createExpense(payload);
      }
      toast.success(editing ? "Expense updated." : "Expense created.");
      reset();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not save the expense."));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable backdrop="static">
      <Form onSubmit={handleSubmit(submit)} noValidate>
        <ModalHeader toggle={toggle}>{editing ? `Edit ${$expense(expense).reference}` : "New expense"}</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Company *</Label>
                <Input
                  type="select"
                  invalid={Boolean(errors.company_id)}
                  {...register("company_id", { required: "Company is required" })}
                >
                  <option value="">Select company…</option>
                  {meta.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Input>
                <FormFeedback>{errors.company_id?.message}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Title *</Label>
                <Input invalid={Boolean(errors.title)} {...register("title", { required: "Title is required" })} />
                <FormFeedback>{errors.title?.message}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Category</Label>
                <Input type="select" {...register("category_id")}>
                  <option value="">No category</option>
                  {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Supplier</Label>
                <Input placeholder="Supplier name" {...register("supplier_name")} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Expense date *</Label>
                <Input type="date" invalid={Boolean(errors.expense_date)} {...register("expense_date", { required: "Date is required" })} />
                <FormFeedback>{errors.expense_date?.message}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Invoice date</Label>
                <Input type="date" {...register("invoice_date")} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Due date</Label>
                <Input type="date" {...register("due_date")} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Net amount *</Label>
                <Input type="number" step="0.001" min="0" invalid={Boolean(errors.net_amount)} {...register("net_amount", { required: "Net amount is required" })} />
                <FormFeedback>{errors.net_amount?.message}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Tax amount</Label>
                <Input type="number" step="0.001" min="0" {...register("tax_amount")} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Gross amount *</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder={calculatedGross == null ? "" : calculatedGross}
                  invalid={Boolean(errors.gross_amount)}
                  {...register("gross_amount", {
                    validate: (value) => {
                      if (value !== undefined && value !== null && value !== "") return true;
                      const n = Number(net);
                      return Number.isFinite(n) && n > 0 ? true : "Enter net or gross amount";
                    },
                  })}
                />
                <FormFeedback>{errors.gross_amount?.message}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Currency *</Label>
                <Input type="select" {...register("currency_code", { required: true })}>
                  {meta.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Payment method</Label>
                <Input type="select" {...register("payment_method")}>
                  <option value="">—</option>
                  {meta.payment_methods.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Department</Label>
                <Input {...register("department")} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Cost center</Label>
                <Input {...register("cost_center")} />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label>Description</Label>
                <Input type="textarea" rows={2} {...register("description")} />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>Cancel</Button>
          <Button color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : editing ? "Save changes" : "Create expense"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

/** Guard against undefined/null in a stable, testable way without importing the model. */
function $expense(value) {
  return value && typeof value === "object" ? value : {};
}